/**
 * FAQ Page（Figma 9569-25099 / 10072-32623, 10072-33263）
 * カテゴリナビの is-active をハッシュ・スクロール位置と同期 / SP: Summaryモーダル
 *
 * @package Spice_Theme
 */

import { gsap } from 'gsap';

function faqPrefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * @param {string} hash `#faq-cat-...` 形式
 * @returns {boolean} 対象要素があれば true
 */
function scrollFaqToHash(hash) {
  if (!hash || !hash.startsWith('#')) {
    return false;
  }
  const id = hash.slice(1);
  const el = document.getElementById(id);
  if (!el || !el.classList.contains('js-faq-category')) {
    return false;
  }
  el.scrollIntoView({
    behavior: faqPrefersReducedMotion() ? 'auto' : 'smooth',
    block: 'start',
  });
  return true;
}

function setActive(slug) {
  const links = document.querySelectorAll('.js-faq-nav-link');
  links.forEach((link, i) => {
    const linkSlug = (link.getAttribute('href') || '').replace('#faq-cat-', '');
    const active = linkSlug === slug || (slug === '' && i === 0);
    link.classList.toggle('is-active', active);
  });
}

// 上端を越えた .js-faq-category のうち DOM 順で最後の slug（なければ '' → setActive で先頭）
function getSlugByScroll() {
  const sections = document.querySelectorAll('.js-faq-category');
  let slug = '';
  sections.forEach(el => {
    if (el.getBoundingClientRect().top <= 0) {
      slug = el.id.replace('faq-cat-', '');
    }
  });
  return slug;
}

function initSummaryModal() {
  const overlay = document.getElementById('js-faq-summary-modal');
  const summaryBtn = document.querySelector('.js-faq-summary-btn');
  if (!overlay || !summaryBtn) return;

  function openModal() {
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    summaryBtn.setAttribute('aria-expanded', 'true');
  }

  function closeModal() {
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    summaryBtn.setAttribute('aria-expanded', 'false');
  }

  summaryBtn.addEventListener('click', openModal);
  overlay
    .querySelector('.js-faq-close-modal')
    ?.addEventListener('click', closeModal);
  overlay.addEventListener('click', e => {
    if (e.target === overlay) closeModal();
  });

  overlay.querySelectorAll('.js-faq-nav-link').forEach(link => {
    link.addEventListener('click', () => {
      closeModal();
    });
  });
}

function initFaqAccordion() {
  if (faqPrefersReducedMotion()) return;

  document.querySelectorAll('.p-faq .c-faq__item').forEach(item => {
    const trigger = item.querySelector('.c-faq__trigger');
    const answer = item.querySelector('.c-faq__answer');
    if (!trigger || !answer) return;

    if (item.hasAttribute('open')) {
      gsap.set(answer, { height: 'auto', overflow: '' });
    }

    trigger.addEventListener('click', e => {
      e.preventDefault();
      gsap.killTweensOf(answer);

      if (!item.hasAttribute('open')) {
        item.setAttribute('open', '');
        gsap.set(answer, { height: 0, overflow: 'hidden' });
        gsap.to(answer, {
          height: 'auto',
          duration: 0.4,
          ease: 'power3.out',
          onComplete: () => gsap.set(answer, { height: '', overflow: '' }),
        });
      } else {
        gsap.set(answer, { height: answer.offsetHeight, overflow: 'hidden' });
        gsap.to(answer, {
          height: 0,
          duration: 0.3,
          ease: 'power3.in',
          onComplete: () => {
            item.removeAttribute('open');
            gsap.set(answer, { height: '', overflow: '' });
          },
        });
      }
    });
  });
}

function initFaqPage() {
  const root = document.querySelector('.p-faq');
  if (!root) return;

  const slugFromHash = () =>
    (window.location.hash || '').replace('#faq-cat-', '');

  setActive(window.location.hash ? slugFromHash() : getSlugByScroll());

  root.addEventListener('click', e => {
    const a = e.target.closest('a.js-faq-nav-link');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href || !href.startsWith('#faq-cat-')) return;
    e.preventDefault();
    if (scrollFaqToHash(href)) {
      history.pushState(null, '', href);
      setActive(href.replace('#faq-cat-', ''));
    }
  });

  let ticking = false;
  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setActive(getSlugByScroll());
        ticking = false;
      });
    },
    { passive: true }
  );

  window.addEventListener('hashchange', () => {
    setActive(slugFromHash());
    scrollFaqToHash(window.location.hash);
  });

  initSummaryModal();
  initFaqAccordion();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFaqPage);
} else {
  initFaqPage();
}
